import { push, replace } from 'connected-react-router';
import { SubmissionError } from 'redux-form';
import { QUANTITY_INPUT } from 'jl-design-system/form/fields';
import { setFieldValue } from 'jl-design-system/redux/actions/form/formActions';
// lodash
import get from 'lodash/get';
import find from 'lodash/find';
import startsWith from 'lodash/startsWith';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';

import {
  ANALYTICS_EDIT_BASKET_MODAL_OPEN,
  SET_EDIT_BASKET_BASE_ROUTE,
  REBATCH_ORDER,
  PUT_ITEM_QUANTITY,
  PUT_ITEMS_QUANTITIES,
  ANALYTICS_AMEND_BASKET_MODAL_OPEN,
  SHOW_ITEM_REMOVE_OVERLAY,
  HIDE_ITEM_REMOVE_OVERLAY,
  REMOVE_BASKET_ITEMS,
  GET_ITEMS,
  BASKET_ITEM_OUT_OF_STOCK,
  SHOW_BATCHING_FAILURE_MODAL,
  ANALYTICS_BATCH_ERROR,
  ANALYTICS_BATCH_ERROR_ITEMS_REMOVED,
  SHOW_REMOVE_BATCHING_FAILURE_ERROR,
  ANALYTICS_POS_CREDIT_INELIGIBLE_ITEMS_REMOVED,
  ANALYTICS_SHOW_COLLECTION_HELP_REMOVE,
} from '../../../constants/actionConstants';
import {
  URL_PUT_ITEM_QUANTITY,
  URL_PUT_ITEMS_QUANTITIES,
  URL_GET_ITEMS,
  URL_REBATCH_ORDER,
  URL_REMOVE_ITEMS_FROM_BASKET,
  URL_REMOVE_TRADE_IN_ITEM,
} from '../../../constants/endpointConstants';
import routeConstants from '../../../constants/routeConstants';
import deliveryConstants from '../../../constants/deliveryConstants';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import { triggerAnalyticsEvent } from '../analytics/analyticsAction';
import { showDisableSiteSpinner, showEmptyBasketNotification } from '../app/appActions';
import { hidePOSCreditBasketAmendModal } from '../payment/paymentActions';
import {
  findCollectionPoint,
  selectCollectionPoint,
  getSavedCollectionPoints,
} from '../click-and-collect/clickAndCollectActions';
import { isApps } from '../../reducers/app/appReducer';
import {
  initDeliveryPage,
  getDeliveryPage,
  refreshDeliveryMethods,
  setDeliveryAddressAndGetDeliveryMethods,
} from '../delivery/deliveryActions';

import getErrorMessageObject from '../../../utils/error/getErrorMessageObject';
import { getFormId } from '../../../utils/form/configs/itemQuantity';
import isItemFabric from '../../../utils/orderform/isItemFabric';
import { handbackTo } from '../data-action-link/dataActionLinkActions';
import { shouldShowCollectionHelpText } from '../../../utils/collection/collectionHelp';
import { isPaymentPage } from '../../../utils/helpers/pageType';

export const showEditBasketModal = baseRoutePath => (dispatch) => {
  dispatch({
    type: SET_EDIT_BASKET_BASE_ROUTE,
    baseRoutePath,
  });

  dispatch(push(routeConstants.EDIT_BASKET));
  dispatch(triggerAnalyticsEvent(ANALYTICS_EDIT_BASKET_MODAL_OPEN));
};

export const rebatchOrder = address => ({
  type: REBATCH_ORDER,
  request: client => client({
    path: URL_REBATCH_ORDER,
    config: { method: 'POST' },
  }),
  address,
  orderHasBeenRebatched: true,
});

export const rebatchOrderAfterBasketEdit = () => async (dispatch, getState) => {
  const orderNeedsToBeRebatched = get(getState(), 'orderForm.orderNeedsToBeRebatched');
  const address = get(getState(), 'delivery.confirmedDeliveryAddress');
  const selectedDeliveryChoiceId = get(getState(), 'delivery.selectedDeliveryChoiceId');
  const pathname = get(getState().router, 'location.pathname', '');
  const editFromPaymentPage = isPaymentPage(pathname);

  if (orderNeedsToBeRebatched) {
    dispatch(showDisableSiteSpinner());

    const response = await dispatch(rebatchOrder(address));

    if (response.type === `${REBATCH_ORDER}.SUCCESS`) {
      const hasConfirmedDeliveryAddress = !!get(getState(), 'delivery.confirmedDeliveryAddress');
      const selectedDeliveryAddress = get(getState(), 'user.selectedDeliveryAddress');

      if (pathname !== routeConstants.DELIVERY_CHOICE) {
        if (selectedDeliveryChoiceId === deliveryConstants.CLICK_AND_COLLECT) {
          dispatch(replace(routeConstants.CLICK_AND_COLLECT));
          const searchTerm = get(getState(), 'clickAndCollect.collectionPointSearchTerm');
          const selectedCollectionPoint = get(getState(), 'clickAndCollect.selectedCollectionPoint');
          const savedCollectionPoints = get(getState(), 'user.collectionPoints');


          if (!isEmpty(savedCollectionPoints)) {
            // refresh saved collection points as collection charges may have changed
            await dispatch(getSavedCollectionPoints());
          }

          if (searchTerm) {
            // refresh collection point search results as collection charges may have changed
            await dispatch(findCollectionPoint(searchTerm, true));
          }

          if (selectedCollectionPoint) {
            // re-select selected collection point to get latest dates & collection charges
            await dispatch(selectCollectionPoint({
              collectionPoint: selectedCollectionPoint,
              refresh: true,
            }));
          }

        } else if (hasConfirmedDeliveryAddress) {
          if (editFromPaymentPage) {
            await dispatch(replace(routeConstants.DELIVERY_OPTIONS));
          } else await dispatch(refreshDeliveryMethods());
        } else if (selectedDeliveryAddress) {
          await dispatch(setDeliveryAddressAndGetDeliveryMethods({
            address: selectedDeliveryAddress,
          }));
        }
      }

      if (!editFromPaymentPage) {
        dispatch(getDeliveryPage({
          isRebatch: true,
        }));
      }
    }
  }
};

export const showItemRemoveOverlay = itemId => ({
  type: SHOW_ITEM_REMOVE_OVERLAY,
  itemId,
});

export const hideItemRemoveOverlay = itemId => ({
  type: HIDE_ITEM_REMOVE_OVERLAY,
  itemId,
});

export const getItems = () => ({
  type: GET_ITEMS,
  request: client => client({
    path: URL_GET_ITEMS,
    config: { method: 'GET' },
  }),
});

export const removeItems = (itemIds, isTradeIn = false) => {
  const itemIdsString = itemIds.toString();
  let path = URL_REMOVE_ITEMS_FROM_BASKET(itemIdsString);

  if (isTradeIn) {
    path = URL_REMOVE_TRADE_IN_ITEM(itemIds);
  }

  return {
    type: REMOVE_BASKET_ITEMS,
    request: client => client({ path, config: { method: 'DELETE' } }),
    itemIds,
    products: isArray(itemIds) ? itemIds.map(id => ({ productId: id })) : undefined,
    isTradeIn,
  };
};

export const handleRemoveItem = (itemId, lastItemInBasket, isChildItem, isTradeIn) => async (dispatch, getState) => {
  const stateBeforeRemove = getState();

  const response = await dispatch(removeItems([itemId], isTradeIn));

  if (response.type === `${REMOVE_BASKET_ITEMS}.SUCCESS`) {
    if (shouldShowCollectionHelpText({
      id: itemId,
      isChildItem,
      state: stateBeforeRemove,
    })) {
      dispatch(triggerAnalyticsEvent(ANALYTICS_SHOW_COLLECTION_HELP_REMOVE));
    }

    const state = getState();
    if (lastItemInBasket) {
      const browseUrl = get(state, 'bff.browseUrl');
      dispatch(handbackTo(browseUrl));

      if (isApps(state)) {
        dispatch(showEmptyBasketNotification());
      }
    } else {
      dispatch(getItems());
    }
  }
};

export const updateItemQuantity = ({
  itemId,
  quantity,
}) => async (dispatch, getState) => {

  const showEditBasketModal = get(getState(), 'delivery.showEditBasketModal');

  if (showEditBasketModal) {
    const response = await dispatch({
      type: PUT_ITEM_QUANTITY,
      request: client => client({
        path: URL_PUT_ITEM_QUANTITY(itemId),
        config: {
          method: 'PUT', body: { quantity },
        },
      }),
      itemId,
    });

    if (response.type === `${PUT_ITEM_QUANTITY}.FAILED`) {

      if (response.error) {

        const errorCode = get(response.error, 'code');

        if (errorCode === errorCodeConstants.ITEM_NOT_IN_STOCK) {
          dispatch({
            type: BASKET_ITEM_OUT_OF_STOCK,
            itemId,
          });
        }

        const getItemsResponse = await dispatch(getItems());

        if (
          errorCode === errorCodeConstants.ITEM_QUANTITY_PURCHASE_LIMIT ||
          errorCode === errorCodeConstants.QUANTITY_REDUCED_TO_AVAILABLE_STOCK ||
          errorCode === errorCodeConstants.ALL_AVAILABLE_STOCK_ALREADY_ADDED ||
          errorCode === errorCodeConstants.ITEM_QUANTITY_INVALID
        ) {
          const items = get(getItemsResponse.result, 'orderForm.items');
          const item = find(items, { id: itemId });
          const quantity = item.quantity;
          const formId = getFormId(itemId);

          // ensure field value is updated with correct quantity
          await dispatch(setFieldValue(formId, [QUANTITY_INPUT.id], quantity.toString()));

          const error = getErrorMessageObject({
            error: response.error,
            vars: {
              isItemFabric: isItemFabric(item),
              stockLevel: get(item, 'availability.stockLevel'),
              quantityLimit: get(item, 'availability.quantityLimit'),
            },
          });

          throw new SubmissionError({ itemQuantity: error.message });
        }
      }
    }
  }
};

export const batchUpdateItemQuantity = itemsQuantity => ({
  type: PUT_ITEMS_QUANTITIES,
  request: client => client({
    path: URL_PUT_ITEMS_QUANTITIES,
    config: {
      method: 'PUT',
      body: {
        itemsQuantity,
      },
    },
  }),
});

export const showLimitedStockModal = (baseRoutePath, analyticsAction) => (dispatch) => {
  dispatch({
    type: SET_EDIT_BASKET_BASE_ROUTE,
    baseRoutePath,
  });
  if (analyticsAction) {
    dispatch(triggerAnalyticsEvent(analyticsAction));
  } else {
    dispatch(triggerAnalyticsEvent(ANALYTICS_AMEND_BASKET_MODAL_OPEN));
  }
};

export const showBatchingFailureModal = batchingFailureIds => async (dispatch, getState) => {
  const getItemsResponse = await dispatch(getItems());
  if (getItemsResponse.type === `${GET_ITEMS}.SUCCESS`) {
    const items = get(getItemsResponse, 'result.orderForm.items', []);
    const batchingFailureItems = filter(items, item => includes(batchingFailureIds, item.id));
    const allItemsHaveBatchingFailure = items.length === batchingFailureIds.length;

    dispatch({
      type: SHOW_BATCHING_FAILURE_MODAL,
      batchingFailureItems,
      allItemsHaveBatchingFailure,
    });

    const analyticsProducts = get(getState().analytics, 'analyticsData.checkout.product');

    dispatch(triggerAnalyticsEvent(ANALYTICS_BATCH_ERROR, {
      products: batchingFailureItems.map(item => find(analyticsProducts, { productId: item.productId })),
    }));
  }
};

export const removeBatchingFailureItems = items => async (dispatch, getState) => {
  const itemIds = items.map(item => (item.id));
  const getItemsResponse = await dispatch(removeItems(itemIds));

  if (getItemsResponse.type === `${REMOVE_BASKET_ITEMS}.SUCCESS`) {
    const analyticsProducts = get(getState().analytics, 'analyticsData.checkout.product');
    const removedProducts = items.map(item => find(analyticsProducts, { productId: item.productId }));

    dispatch(triggerAnalyticsEvent(ANALYTICS_BATCH_ERROR_ITEMS_REMOVED, { products: removedProducts }));
    dispatch(initDeliveryPage());
  } else {
    dispatch({ type: SHOW_REMOVE_BATCHING_FAILURE_ERROR });
  }
};

export const removeItemsFromBasket = itemIds => async (dispatch, getState) => {

  const response = await dispatch(removeItems(itemIds));

  const pathname = get(getState().router, 'location.pathname', '');
  const { type } = response;

  const address = get(getState().delivery, 'confirmedDeliveryAddress');
  await dispatch(rebatchOrder(address));

  if (type === `${REMOVE_BASKET_ITEMS}.SUCCESS`) {
    // remove from out of stock modal
    if (startsWith(pathname, routeConstants.PAYMENT)) {
      dispatch(push(routeConstants.DELIVERY));
    }

    if (pathname === routeConstants.DELIVERY_CHOICE) {
      dispatch(getDeliveryPage({ isRebatch: true }));
    }
  }
};

export const removePOSCreditIneligibleItems = () => async (dispatch, getState) => {
  const itemIds = get(getState(), 'orderForm.posCreditIneligibleItemIds', []);
  const response = await dispatch(removeItems(itemIds));
  const { type } = response;

  const address = get(getState().delivery, 'confirmedDeliveryAddress');
  await dispatch(rebatchOrder(address));

  if (type === `${REMOVE_BASKET_ITEMS}.SUCCESS`) {
    dispatch(triggerAnalyticsEvent(ANALYTICS_POS_CREDIT_INELIGIBLE_ITEMS_REMOVED, {
      products: itemIds.map(productId => ({
        productId,
      })),
    }));

    dispatch(hidePOSCreditBasketAmendModal());
    dispatch(push(routeConstants.DELIVERY));
  }
};
